import { useEffect } from "react";
import { DatePicker, Form, Input, Modal, Select, Typography } from "antd";
import dayjs, { type Dayjs } from "dayjs";

import {
  COLLECTIONS_STAGE_LABELS,
  CollectionsStage,
  type ProductClientCollections,
} from "@fitness-app/data-models/entities/ProductClient";

import { type UpdateCollectionsStagePayload } from "./useUpdateCollectionsStage";

interface CollectionsStageModalProps {
  open: boolean;
  loading?: boolean;
  client: { productClientId: string; clientName: string | null; clientEmail: string } | null;
  current: ProductClientCollections | null;
  onCancel: () => void;
  onSubmit: (payload: UpdateCollectionsStagePayload) => void;
}

type FormModel = {
  stage: CollectionsStage;
  nextContactAt: Dayjs | null;
  note: string;
};

const STAGE_OPTIONS = Object.values(CollectionsStage).map((stage) => ({
  value: stage,
  label: COLLECTIONS_STAGE_LABELS[stage],
}));

/**
 * One dialog for the whole collections process: pick where the case is now,
 * say when to come back to it, and write down what happened. The note lands in
 * the client's timeline, so the history Anita kept in a spreadsheet lives next
 * to the client from now on.
 */
const CollectionsStageModal = ({ open, loading, client, current, onCancel, onSubmit }: CollectionsStageModalProps) => {
  const [form] = Form.useForm<FormModel>();

  useEffect(() => {
    if (open) {
      form.setFieldsValue({
        stage: current?.stage ?? CollectionsStage.New,
        nextContactAt: current?.nextContactAt ? dayjs(current.nextContactAt) : null,
        note: "",
      });
    }
  }, [open, current, form]);

  const handleOk = async () => {
    if (!client) {
      return;
    }

    const values = await form.validateFields();
    onSubmit({
      productClientId: client.productClientId,
      stage: values.stage,
      note: values.note?.trim() || undefined,
      nextContactAt: values.nextContactAt ? values.nextContactAt.toISOString() : null,
    });
  };

  return (
    <Modal
      open={open}
      title="Etap windykacji"
      okText="Zapisz"
      cancelText="Anuluj"
      confirmLoading={loading}
      onCancel={onCancel}
      onOk={() => void handleOk()}
      destroyOnClose
    >
      {client ? (
        <Typography.Paragraph type="secondary" className="mb-4">
          {client.clientName ?? client.clientEmail}
          {client.clientName ? <span className="ml-2 text-xs">{client.clientEmail}</span> : null}
        </Typography.Paragraph>
      ) : null}
      <Form form={form} layout="vertical">
        <Form.Item name="stage" label="Etap" rules={[{ required: true, message: "Wybierz etap" }]}>
          <Select options={STAGE_OPTIONS} showSearch optionFilterProp="label" />
        </Form.Item>
        <Form.Item name="nextContactAt" label="Następny kontakt" extra="Kiedy wrócić do sprawy — telefon, mail, SMS.">
          <DatePicker className="w-full" format="DD.MM.YYYY" allowClear />
        </Form.Item>
        <Form.Item
          name="note"
          label="Co się wydarzyło"
          extra="Rozmowa, ustalenia, wysłane pismo. Trafia do notatek klientki."
        >
          <Input.TextArea rows={3} maxLength={2000} showCount />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CollectionsStageModal;
